<template>
  <beautiful-chat
    id="chat"
    :title="title"
    :class="{ 'title-hidden': !title && !getParticipants[0].id }"
    :participants="getParticipants"
    :titleImageUrl="titleImageUrl"
    :onMessageWasSent="onMessageWasSent"
    :messageList="messagesList"
    :newMessagesCount="unreadMessagesCount"
    :isOpen="isChatOpen"
    :close="closeChat"
    :open="openChat"
    :showEmoji="true"
    :showFile="false"
    :showTypingIndicator="showTypingIndicator"
    :colors="colors"
    :alwaysScrollToBottom="alwaysScrollToBottom"
    :messageStyling="messageStyling"
  ></beautiful-chat>
</template>

<script>
  import { mapGetters } from 'vuex';
  import ChatParticipant from './chat-participant';
  import sound from '../../services/sound';

  export default {
    props: {
      title: {
        type: String,
      },
      // the list of the messages to show, can be
      // paginated and adjusted dynamically
      messagesList: {
        type: Array,
        required: true,
      },
      participants: {
        type: Array,
        required: true,
      },
    },
    data: () => ({
      inputRef: null,
      isInit: false,
      readMessagesCount: 0,
      unreadMessagesCount: 0,
      titleImageUrl: '',
      // titleImageUrl: './img/meta/android-chrome-192x192.png',
      // to determine whether the chat window should be open or closed
      isChatOpen: false,
      // when set to a value matching the participant.id it
      // shows the typing indicator for the specific user
      showTypingIndicator: '',
      // specifies the color scheme for the component
      colors: {
        header: {
          bg: '#4e8cff',
          text: '#ffffff',
        },
        launcher: {
          bg: '#4e8cff',
        },
        messageList: {
          bg: '#ffffff',
        },
        sentMessage: {
          bg: '#4e8cff',
          text: '#ffffff',
        },
        receivedMessage: {
          bg: '#eaeaea',
          text: '#222222',
        },
        userInput: {
          bg: '#f4f7f9',
          text: '#565867',
        },
      },
      // when set to true always scrolls the chat to the bottom
      // when new events are in (new message, user starts typing...)
      alwaysScrollToBottom: true,
      // enables *bold* /emph/ _underline_ and such (more
      // info at github.com/mattezza/msgdown)
      messageStyling: true,
    }),
    watch: {
      '$route.query': function (query) {
        if (window.innerWidth <= 450) {
          this.isChatOpen = !!query.isChatOpen;
        }
      },
      isChatOpen: function (value) {
        this.$store.commit('layout/setChatIsOpened', value);

        // TODO: rethink
        // setTimeout(() => this.inputRef && this.inputRef.focus(), 100);
      },
      messagesList(messages) {
        if (this.isChatOpen) {
          this.readMessagesCount = messages.length;

          return;
        }

        this.unreadMessagesCount = messages.length - this.readMessagesCount;

        if (this.unreadMessagesCount && this.isInit) {
          sound.play('message');
        }

        this.isInit = true;
      },
    },
    computed: {
      ...mapGetters('auth', ['user']),
      getParticipants() {
        const participants = this.participants
          .filter((p) => p.id !== this.user.id)
          .map((p) => new ChatParticipant(p.id, p.displayName, p.gravatarUrl));

        return !participants.length ? [new ChatParticipant(null, null, null)] : participants;
      },
    },
    methods: {
      onMessageWasSent(message) {
        this.$emit('sendMessage', message);
      },
      // called when the user clicks on the fab button to open the chat
      openChat() {
        this.isChatOpen = true;

        if (window.innerWidth <= 450) {
          this.$router.push({
            query: { ...this.$route.query, isChatOpen: this.isChatOpen },
          });
        }

        this.readMessagesCount = this.messagesList.length;
        this.unreadMessagesCount = 0;
      },
      // called when the user clicks on the botton to close the chat
      closeChat() {
        this.isChatOpen = false;

        if (window.innerWidth <= 450) {
          const query = { ...this.$route.query };
          delete query['isChatOpen'];

          this.$router.replace({ query });
        }
      },
    },
    created() {
      this.isChatOpen = !!this.$route.query.isChatOpen;
    },
    mounted() {
      this.inputRef = document.querySelector('#chat [contenteditable="true"]');
    },
  };
</script>

<style lang="scss">
  $messageListPadding: 16px;
  $headerHeight: 56px;

  #chat.title-hidden {
    .sc-header--title {
      opacity: 0;
      pointer-events: none;
    }
  }

  #chat {
    text-align: left;
    position: relative;

    // TODO: handle media 450px-480px gap
    .sc-chat-window {
      z-index: z('topmost');
      @include phone-lg() {
        z-index: z('chat');
      }
    }

    .sc-header {
      min-height: 0;
      border-top-left-radius: 4px;
      border-top-right-radius: 4px;
      padding: 0;
      padding-left: 16px;
      height: $header-height;
    }

    .sc-header--img {
      padding: 0;
    }

    .sc-header--team-name {
      &:hover {
        background: none;
      }
    }

    .sc-header--title {
      padding: 0 8px 0 0;

      &:hover {
        box-shadow: none;
      }
    }

    .sc-header--close-button {
      height: 100%;
      width: 56px;
      margin: 0;
      position: relative;

      &:hover {
        background: none;
        box-shadow: none;
      }

      img {
        position: absolute;
        top: 50%;
        right: 16px;
        transform: translateY(-50%);
        //
        padding: 0;
        width: 20px;
        height: 20px;
      }
    }

    .sc-message-list {
      height: calc(100% - #{$messageListPadding * 2});
      padding: $messageListPadding;
    }

    .sc-message {
      width: 100%;
      padding: 0;
      margin-bottom: 8px;
    }

    .sc-message--text {
      padding: 4px 8px;

      p {
        margin: 0;
      }
    }

    .sc-message--meta {
      text-align: left;
      margin-top: 8px !important;
      font-size: 14px;
      font-style: italic;
    }

    .sc-message--emoji {
      font-size: 24px;
      height: 24px;
      width: 24px;
    }

    .sc-user-input {
      border-bottom-left-radius: 4px;
      border-bottom-right-radius: 4px;
    }

    .sc-user-input--text {
      padding: 16px;
    }

    .sc-user-input--buttons {
      position: static;
    }

    .sc-user-input--button {
      position: static;
      width: auto;
      margin: 0;
    }

    .sc-user-input--send-icon-wrapper {
      padding: 16px;
    }

    .sc-emoji-picker--content {
      padding: 10px 5px;
    }

    .sc-launcher {
      position: fixed;
      width: 48px;
      height: 48px;
      bottom: 16px;
      right: 16px;

      &::before {
        width: 100%;
        height: 100%;
      }

      img {
        width: 48px;
        height: 48px;
        //
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
    }

    .sc-open-icon {
      padding: 14px;
    }
  }
</style>
